import { colors } from '@kmns-internal/furikake'
import { Box, Button, Stack, Typography } from '@mui/material'
import type React from 'react'

export interface IndustryCardInfo {
  svgComponent?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>
  title: string
}

interface Props<T extends IndustryCardInfo> {
  showIndustries: T[][]
  onClick: (industry: T) => void
  smColumns?: number
  buttonJustifyContent?: 'flex-start' | 'space-between'
  renderCardContent?: (industry: T) => React.ReactNode
}

function defaultRenderCardContent<T extends IndustryCardInfo>(
  industry: T,
): React.ReactNode {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="center"
      spacing={2}
    >
      {industry.svgComponent && (
        <industry.svgComponent
          width={48}
          height={48}
          style={{
            width: 48,
            height: 48,
            fill: colors.primary.main,
            color: colors.primary.main,
          }}
        />
      )}
      <Typography variant="body1" fontWeight="bold">
        {industry.title}
      </Typography>
    </Stack>
  )
}

function IndustryCardGrid<T extends IndustryCardInfo>({
  showIndustries,
  onClick,
  smColumns = 3,
  buttonJustifyContent = 'flex-start',
  renderCardContent = defaultRenderCardContent,
}: Props<T>) {
  return (
    <>
      {showIndustries.map(industries => (
        <Box
          key={industries.map(i => i.title).join('-')}
          sx={{ justifyContent: 'center', display: 'flex' }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(1, minmax(0, 1fr))',
                sm: `repeat(${smColumns}, minmax(0, 1fr))`,
              },
              gap: 2,
              px: { xs: 2, sm: 0 },
              width: { xs: '100%', sm: '70%' },
            }}
          >
            {industries.map(industry => (
              <Box key={industry.title}>
                <Button
                  variant="outlined"
                  color="secondary"
                  sx={{
                    minHeight: 88,
                    width: '100%',
                    padding: '12px 24px',
                    justifyContent: buttonJustifyContent,
                  }}
                  onClick={() => onClick(industry)}
                >
                  {renderCardContent(industry)}
                </Button>
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </>
  )
}

export default IndustryCardGrid
